import { useState } from 'react';
import PharmacyHero from '../../components/Pharmacy/PharmacyHero';
import PharmacyStats from '../../components/Pharmacy/PharmacyStats';
import PharmacyModuleNav from '../../components/Pharmacy/PharmacyModuleNav';
import ModuleBlock from '../../components/Modules/ModuleBlock';
import PharmacyMasterShowcase from '../../components/Pharmacy/PharmacyMasterShowcase';
import PharmacyFeatures from '../../components/Pharmacy/PharmacyFeatures';
import Contact from '../../components/Contact';
import ScrollToTop from '../../common/ScrollToTop';
import { usePageMetadata, ROUTE_METADATA } from '../../hooks/usePageMetadata';

import PharmacyDashboardContent from '../../content/PharmacyDashboardContent.json';
import PharmacyCreateInvoiceContent from '../../content/PharmacyCreateInvoiceContent.json';
import PharmacySalesInvoiceContent from '../../content/PharmacySalesInvoice.json';
import PharmacyStockInventoryContent from '../../content/PharmacyStockInventoryContent.json';
import PharmacyAddStockContent from '../../content/PharmacyAddStockContent.json';
import PharmacyPurchaseOrderContent from '../../content/PharmacyPurchaseOrderContent.json';
import PharmacyReturnMedicineContent from '../../content/PharmacyReturnMedicineContent.json';
import PharmacyReportsContent from '../../content/PharmacyReportsContent.json';
import PharmacySettingContent from '../../content/PharmacySettingContent.json';

const Pharmacy = () => {
  usePageMetadata(ROUTE_METADATA['/pharmacy']);
  const [activeModule, setActiveModule] = useState('dashboard');

  const scrollTo = (id: string) => {
    setActiveModule(id);
    const element = document.getElementById(id);
    if (element) {
      const yOffset = -120;
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  const handleRequestDemo = () => {
    scrollTo('contact');
  };

  return (
    <main className="min-h-screen bg-white">
      {/* Floating Scroll to Top button */}
      <ScrollToTop />

      {/* Hero Section */}
      <PharmacyHero />

      {/* Key Numbers / Stats */}
      <PharmacyStats />

      {/* Module Navigation anchor */}
      <div id="modules">
        <PharmacyModuleNav activeModule={activeModule} onSelectModule={scrollTo} />
      </div>

      {/* Core Pharmacy Modules */}
      <div className="space-y-4">
        {/* 1. Dashboard */}
        <ModuleBlock
          id="dashboard"
          category={PharmacyDashboardContent.category}
          title={PharmacyDashboardContent.title}
          content={PharmacyDashboardContent.text}
          contentList={PharmacyDashboardContent.contentList}
          icon="pharmacy_dashboard.png"
          direction="left"
          badge={PharmacyDashboardContent.badge}
          onRequestDemo={handleRequestDemo}
        />

        {/* 2. POS Counter Billing / Create Invoice */}
        <ModuleBlock
          id="create-invoice"
          category={PharmacyCreateInvoiceContent.category}
          title={PharmacyCreateInvoiceContent.title}
          content={PharmacyCreateInvoiceContent.text}
          contentList={PharmacyCreateInvoiceContent.contentList}
          icon="pharmacy_create_invoice.png"
          direction="right"
          badge={PharmacyCreateInvoiceContent.badge}
          onRequestDemo={handleRequestDemo}
        />

        {/* 3. Sales Invoices Register */}
        <ModuleBlock
          id="sales-invoice"
          category={PharmacySalesInvoiceContent.category}
          title={PharmacySalesInvoiceContent.title}
          content={PharmacySalesInvoiceContent.text}
          contentList={PharmacySalesInvoiceContent.contentList}
          icon="pharmacy_sales_invoice.png"
          direction="left"
          badge={PharmacySalesInvoiceContent.badge}
          onRequestDemo={handleRequestDemo}
        />

        {/* 4. Stock Inventory */}
        <ModuleBlock
          id="stock-inventory"
          category={PharmacyStockInventoryContent.category}
          title={PharmacyStockInventoryContent.title}
          content={PharmacyStockInventoryContent.text}
          contentList={PharmacyStockInventoryContent.contentList}
          icon="pharmacy_stock_inventory.png"
          direction="right"
          badge={PharmacyStockInventoryContent.badge}
          onRequestDemo={handleRequestDemo}
        />

        {/* 5. Inward GRN / Add Stock */}
        <ModuleBlock
          id="add-stock"
          category={PharmacyAddStockContent.category}
          title={PharmacyAddStockContent.title}
          content={PharmacyAddStockContent.text}
          contentList={PharmacyAddStockContent.contentList}
          icon="pharmacy_add_stock.png"
          direction="left"
          badge={PharmacyAddStockContent.badge}
          onRequestDemo={handleRequestDemo}
        />

        {/* 6. Purchase Orders */}
        <ModuleBlock
          id="purchase-order"
          category={PharmacyPurchaseOrderContent.category}
          title={PharmacyPurchaseOrderContent.title}
          content={PharmacyPurchaseOrderContent.text}
          contentList={PharmacyPurchaseOrderContent.contentList}
          icon="pharmacy_purchase_order.png"
          direction="right"
          badge={PharmacyPurchaseOrderContent.badge}
          onRequestDemo={handleRequestDemo}
        />

        {/* 7. Medicine Returns & Credits */}
        <ModuleBlock
          id="return-medicine"
          category={PharmacyReturnMedicineContent.category}
          title={PharmacyReturnMedicineContent.title}
          content={PharmacyReturnMedicineContent.text}
          contentList={PharmacyReturnMedicineContent.contentList}
          icon="pharmacy_return_medicine.png"
          direction="left"
          badge={PharmacyReturnMedicineContent.badge}
          onRequestDemo={handleRequestDemo}
        />

        {/* 8. Master Catalog Showcase (Unit, HSN, Supplier, Location, Manufacturer) */}
        <PharmacyMasterShowcase onRequestDemo={handleRequestDemo} />

        {/* 9. Reports & GST Analytics */}
        <ModuleBlock
          id="reports"
          category={PharmacyReportsContent.category}
          title={PharmacyReportsContent.title}
          content={PharmacyReportsContent.text}
          contentList={PharmacyReportsContent.contentList}
          icon="pharmacy_reports.png"
          direction="right"
          badge={PharmacyReportsContent.badge}
          onRequestDemo={handleRequestDemo}
        />

        {/* 10. Store Settings & Regulatory */}
        <ModuleBlock
          id="settings"
          category={PharmacySettingContent.category}
          title={PharmacySettingContent.title}
          content={PharmacySettingContent.text}
          contentList={PharmacySettingContent.contentList}
          icon="pharmacy_settings.png"
          direction="left"
          badge={PharmacySettingContent.badge}
          onRequestDemo={handleRequestDemo}
        />
      </div>

      {/* Why Choose Divine Pharmacy Features */}
      <PharmacyFeatures />

      {/* Contact & Live Demo Request Form */}
      <Contact />
    </main>
  );
};

export default Pharmacy;
